import { envConfig } from '@/shared/config/env'
import {
  filterPostHogCaptureByIdentityGate,
  getPostHogIdentityCaptureState,
  isPostHogIdentityCaptureCurrent,
  setPostHogIdentityCaptureAllowed,
  setPostHogIdentityCapturePending,
} from '@/shared/analytics/posthogIdentity'
import { logger } from '@/utils/logger'

const hasPostHogKey = Boolean(envConfig.posthogKey)

type PostHogClient = typeof import('posthog-js').default

type PostHogFacade = {
  capture: (...args: Parameters<PostHogClient['capture']>) => void
  clear_pending_captures: () => void
  flush_pending_captures: () => void
  get_identified_user_id: () => Promise<unknown>
  get_explicit_consent_status: () => Promise<
    ReturnType<PostHogClient['get_explicit_consent_status']>
  >
  identify: (...args: Parameters<PostHogClient['identify']>) => Promise<void>
  is_client_loaded: () => boolean
  is_identity_capture_current: (generation: number) => boolean
  opt_in_capturing: (...args: Parameters<PostHogClient['opt_in_capturing']>) => Promise<void>
  opt_out_capturing: (...args: Parameters<PostHogClient['opt_out_capturing']>) => Promise<void>
  register: (...args: Parameters<PostHogClient['register']>) => Promise<void>
  reset: (...args: Parameters<PostHogClient['reset']>) => Promise<void>
  set_identity_capture_allowed: (allowed: boolean) => void
  set_identity_capture_pending: () => number
  startSessionRecording: (
    ...args: Parameters<PostHogClient['startSessionRecording']>
  ) => Promise<void>
  stopSessionRecording: (
    ...args: Parameters<PostHogClient['stopSessionRecording']>
  ) => Promise<void>
}

const MAX_PENDING_CAPTURES = 100
let pendingCaptures: Parameters<PostHogClient['capture']>[] = []
let posthogClient: PostHogClient | null = null
let posthogClientPromise: Promise<PostHogClient | null> | null = null
let posthogLoadFailureLogged = false
let posthogCaptureFailureLogged = false

const loadPostHogClient = async () => {
  if (!hasPostHogKey) return null
  if (posthogClientPromise) return posthogClientPromise

  posthogClientPromise = import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(envConfig.posthogKey, {
        api_host: envConfig.posthogHost,
        ui_host: envConfig.posthogUiHost,
        defaults: '2026-05-30',
        capture_pageview: false,
        capture_pageleave: false,
        disable_session_recording: true,
        session_recording: {
          maskAllInputs: false,
        },
        strict_script_versioning: true,
        before_send: filterPostHogCaptureByIdentityGate,
      })
      posthogClient = posthog
      return posthog
    })
    .catch((error: unknown) => {
      if (!posthogLoadFailureLogged) {
        posthogLoadFailureLogged = true
        logger.warn(
          '[PostHog] SDK initialization failed',
          error instanceof Error ? error.message : 'unknown error',
        )
      }
      posthogClientPromise = null
      return null
    })

  return posthogClientPromise
}

const noop = (..._args: unknown[]) => undefined
const asyncNoop = async (..._args: unknown[]) => undefined

const queuePendingCapture = (args: Parameters<PostHogClient['capture']>) => {
  if (pendingCaptures.length >= MAX_PENDING_CAPTURES) pendingCaptures.shift()
  pendingCaptures.push(args)
}

const logPostHogCaptureFailure = (error: unknown) => {
  if (posthogCaptureFailureLogged) return
  posthogCaptureFailureLogged = true
  logger.warn(
    '[PostHog] Event capture failed',
    error instanceof Error ? error.message : 'unknown error',
  )
}

const runWithPostHogClient = async (operation: (posthog: PostHogClient) => void) => {
  if (posthogClient) {
    operation(posthogClient)
    return
  }
  const posthog = await loadPostHogClient()
  if (!posthog) throw new Error('PostHog SDK is unavailable')
  operation(posthog)
}

const captureWithClient = (args: Parameters<PostHogClient['capture']>) => {
  if (posthogClient) {
    try {
      posthogClient.capture(...args)
    } catch (error) {
      logPostHogCaptureFailure(error)
    }
    return
  }

  void loadPostHogClient()
    .then((posthog) => {
      if (!posthog) return
      const captureState = getPostHogIdentityCaptureState()
      if (captureState === 'allowed') {
        posthog.capture(...args)
      } else if (captureState === 'pending') {
        queuePendingCapture(args)
      }
    })
    .catch((error: unknown) => {
      // Capture calls stay fire-and-forget and must not create unhandled
      // rejections. The SDK loader and this path each log only once.
      logPostHogCaptureFailure(error)
    })
}

const clearPendingCaptures = () => {
  pendingCaptures = []
}

const flushPendingCaptures = () => {
  if (getPostHogIdentityCaptureState() !== 'allowed') return
  const captures = pendingCaptures
  pendingCaptures = []
  captures.forEach(captureWithClient)
}

const disabledPostHog: PostHogFacade = {
  capture: noop as PostHogFacade['capture'],
  clear_pending_captures: noop,
  flush_pending_captures: noop,
  get_identified_user_id: async () => null,
  get_explicit_consent_status: async () => 'pending',
  identify: asyncNoop as PostHogFacade['identify'],
  is_client_loaded: () => false,
  is_identity_capture_current: isPostHogIdentityCaptureCurrent,
  opt_in_capturing: asyncNoop as PostHogFacade['opt_in_capturing'],
  opt_out_capturing: asyncNoop as PostHogFacade['opt_out_capturing'],
  register: asyncNoop as PostHogFacade['register'],
  reset: asyncNoop as PostHogFacade['reset'],
  set_identity_capture_allowed: setPostHogIdentityCaptureAllowed,
  set_identity_capture_pending: setPostHogIdentityCapturePending,
  startSessionRecording: asyncNoop as PostHogFacade['startSessionRecording'],
  stopSessionRecording: asyncNoop as PostHogFacade['stopSessionRecording'],
}

const enabledPostHog: PostHogFacade = {
  capture: (...args) => {
    const captureState = getPostHogIdentityCaptureState()
    if (captureState === 'denied') return
    if (captureState === 'pending') {
      queuePendingCapture(args)
      return
    }
    captureWithClient(args)
  },
  clear_pending_captures: clearPendingCaptures,
  flush_pending_captures: flushPendingCaptures,
  get_identified_user_id: async () => {
    const posthog = await loadPostHogClient()
    if (!posthog) throw new Error('PostHog SDK is unavailable')
    return posthog.get_property('$user_id') ?? null
  },
  get_explicit_consent_status: async () => {
    const posthog = await loadPostHogClient()
    return posthog?.get_explicit_consent_status() ?? 'pending'
  },
  identify: (...args) => {
    return runWithPostHogClient((posthog) => {
      posthog.identify(...args)
    })
  },
  is_client_loaded: () => Boolean(posthogClient),
  is_identity_capture_current: isPostHogIdentityCaptureCurrent,
  opt_in_capturing: (...args) => {
    return runWithPostHogClient((posthog) => {
      posthog.opt_in_capturing(...args)
    })
  },
  opt_out_capturing: (...args) => {
    return runWithPostHogClient((posthog) => {
      posthog.opt_out_capturing(...args)
    })
  },
  register: (...args) => {
    return runWithPostHogClient((posthog) => {
      posthog.register(...args)
    })
  },
  reset: (...args) => {
    return runWithPostHogClient((posthog) => {
      posthog.reset(...args)
    })
  },
  set_identity_capture_allowed: setPostHogIdentityCaptureAllowed,
  set_identity_capture_pending: setPostHogIdentityCapturePending,
  startSessionRecording: (...args) => {
    return runWithPostHogClient((posthog) => {
      posthog.startSessionRecording(...args)
    })
  },
  stopSessionRecording: async (...args) => {
    posthogClient?.stopSessionRecording(...args)
  },
}

export function usePostHog() {
  return {
    posthog: hasPostHogKey ? enabledPostHog : disabledPostHog,
    hasPostHogKey,
  }
}
